import { apiFetch } from './http';

/**
 * One flag a person can toggle on a closeup inspection tour's capture
 * (docs/RUNBOOK.md, the review page): the five the platform accepts, kept
 * unioned with `string` so an unknown value the server sent back (a flag
 * added later, or dropped) still renders instead of vanishing.
 */
export type InspectionFlag = 'correct' | 'problem' | 'useless' | 'repeat' | 'mismatch' | string;

/**
 * The platform's own automatic read of a capture (build item 1): {@code empty}
 * (very low edge density or mostly sky/ground), {@code dark}, or {@code
 * duplicate} (its dHash is close to an earlier capture of the same session,
 * named by {@link InspectionCapture.duplicateOfUploadId}). Empty when the
 * watcher sent no stats (an older build, or no Pillow) or nothing was found.
 */
export type QcFlag = 'empty' | 'dark' | 'duplicate' | string;

export interface InspectionCapture {
  uploadId: number;
  stamp: string | null;
  /** The tour's own label for this stop, in Catalan. */
  label: string | null;
  /** «Hauries de veure: …», the tour's own words for what the capture should show. */
  expect: string | null;
  url: string | null;
  takenAt: string | null;
  flags: InspectionFlag[];
  note: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  /** The platform's own classification of this capture; empty when it found nothing worth flagging. */
  qcFlags: QcFlag[];
  /** The earlier upload of this session it was called a duplicate of, when it was. */
  duplicateOfUploadId: number | null;
  /** The runtime tour's own stop kind (base_overview, kit_indoor, ...), null on a build that predates it. */
  stopKind: string | null;
  /** Up to three short log lines from the runtime about the object photographed, null on a build that predates them. */
  report: string[] | null;
  /**
   * Kept with no attestation that it is the game's window (every capture
   * from before the capture guard, 2026-09-27): the platform serves the
   * picture to an admin only, who releases or deletes it.
   */
  withheld: boolean;
  /** The small picture a card draws; `url` is the picture itself, which only the viewer opens. */
  thumbUrl?: string | null;
  /** What the agent saw before anybody looked: shown beside the person's mark, never counted as a review. */
  agentVerdict?: 'correct' | 'problem' | 'useless' | string | null;
  agentNote?: string | null;
  /** `screenshot` (a tour's or a report's) or `hotkey` (a person's own capture). */
  kind?: string | null;
}

/**
 * What a person closes a whole tour with: `reviewed` (looked at, nothing more
 * said, although not every picture carries a mark), `correct`, `problems` or
 * `inconclusive`.
 */
export type TourVerdict = 'reviewed' | 'correct' | 'problems' | 'inconclusive';

/** A tour as a whole: where it stands, what a person decided about it, what it ran on. */
export interface InspectionTour {
  sessionCode: string;
  tray: 'review' | 'done' | 'skip';
  state: string;
  /** Why it needs no review: `superseded`, `old`, `no_captures`, `withheld`, `dismissed`. */
  reason: string | null;
  supersededBy: number | null;
  verdict: TourVerdict | string | null;
  note: string | null;
  /** The comment on the tour as a whole: the base, the composition. */
  comment: string | null;
  dismissReason: string | null;
  dismissed: boolean;
  reopened: boolean;
  decidedBy: string | null;
  updatedAt: string | null;
  platformVersion: string | null;
}

export interface InspectionTourRequest {
  verdict?: TourVerdict | '';
  note?: string;
  comment?: string;
  dismissed?: boolean;
  reopened?: boolean;
  reason?: string;
}

export interface InspectionSessionCaptures {
  sessionCode: string;
  captures: InspectionCapture[];
  /** «Dues forces al mateix punt: X i Y», found by the platform without a person opening either picture. */
  autoFindings: string[];
  /** The campaign this tour belongs to, null for a tour run outside one; links a tour's own page to the campaign's one-page review. */
  campaignId: number | null;
  tour?: InspectionTour | null;
}

export interface InspectionFlagBreakdown {
  total: number;
  reviewed: number;
  correct: number;
  problem: number;
  useless: number;
  repeat: number;
  mismatch: number;
  qcEmpty: number;
  qcDark: number;
  qcDuplicate: number;
}

export interface InspectionGroupSummary {
  key: string;
  label: string;
  counts: InspectionFlagBreakdown;
}

export interface InspectionNoteEntry {
  uploadId: number;
  note: string;
  label: string | null;
  sessionCode: string;
  recipeId: string;
  worldClass: string;
}

export interface InspectionSummary {
  totalCaptures: number;
  totalReviewed: number;
  byRecipe: InspectionGroupSummary[];
  byObjectiveType: InspectionGroupSummary[];
  byStopKind: InspectionGroupSummary[];
  byForceRole: InspectionGroupSummary[];
  notes: InspectionNoteEntry[];
}

export interface InspectionListItem {
  sessionCode: string;
  campaignId: number | null;
  itemIndex: number;
  recipeId: string;
  recipeTitle: string;
  worldClass: string;
  missionCode: string | null;
  date: string | null;
  captures: number;
  reviewed: number;
  /** The first few pictures a page may show, for the list's own card. */
  previews: string[];
  /** How many of the tour's pictures are withheld until an admin looks at them. */
  withheld: number;
  problems?: number;
  tour?: InspectionTour | null;
}

export interface InspectionReview {
  uploadId: number;
  flags: InspectionFlag[];
  note: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

/** Every inspection session a campaign ran (`/proves/inspeccions`), newest first. */
export const fetchInspections = () => apiFetch<InspectionListItem[]>('/api/inspections');

/** One tour's captures, with their label, expect and review in one call (`/proves/inspeccio/:code`). */
export const fetchInspectionReview = (sessionCode: string) =>
  apiFetch<InspectionSessionCaptures>(`/api/inspections/${encodeURIComponent(sessionCode)}/review`);

export const fetchUploadReview = (uploadId: number) =>
  apiFetch<InspectionReview>(`/api/watcher/uploads/${uploadId}/review`);

/** Saved automatically as the flags or the note change on the page. */
export const saveUploadReview = (uploadId: number, flags: InspectionFlag[], note: string) =>
  apiFetch<InspectionReview>(`/api/watcher/uploads/${uploadId}/review`, {
    method: 'PUT',
    body: { flags, note },
  });

/** What a person decides about a whole tour; only what is sent changes. */
export const saveInspectionTour = (sessionCode: string, body: InspectionTourRequest) =>
  apiFetch<InspectionTour>(`/api/inspections/${encodeURIComponent(sessionCode)}/tour`, {
    method: 'PUT',
    body,
  });

/**
 * Every tour of a campaign still waiting for a person, closed as reviewed at
 * once; one already closed or needing no review keeps what it had. Answers the
 * campaign's tours as they stand after.
 */
export const markCampaignReviewed = (campaignId: number | string) =>
  apiFetch<InspectionTour[]>(`/api/inspections/campaigns/${campaignId}/reviewed`, {
    method: 'POST',
  });

/** An admin looked at a withheld picture and lets every page show it. */
export const releaseUpload = (uploadId: number) =>
  apiFetch<unknown>(`/api/watcher/uploads/${uploadId}/release`, { method: 'PUT' });

/** An admin deletes one upload, file and row, for a picture that shows what it never should have. */
export const deleteUpload = (uploadId: number) =>
  apiFetch<null>(`/api/watcher/uploads/${uploadId}`, { method: 'DELETE' });

/** The general correction (`/proves/inspeccions/resum`): every review and QC flag, aggregated. `campaignId` null is every campaign. */
export const fetchInspectionSummary = (campaignId?: number | null) =>
  apiFetch<InspectionSummary>(
    `/api/inspections/summary${campaignId != null ? `?campaign=${encodeURIComponent(campaignId)}` : ''}`,
  );
